import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { Constants } from './const';
import { AccountLoginResponse } from "./itsr-api.model";

@Injectable()
export class SessionStorageService {
  private static lastKnownLanguageCode: string = SessionStorageService.getCurrentLanguageCode()
    || SessionStorageService.determineLanguageCodeFromHostname()
    || Constants.DefaultLanguageCode;

  //
  // Matches the current hostname with a list of pre-defined domain
  // names (English/French) and returns a language code accordingly.
  // In case a match is not found, this method returns null.
  //
  private static determineLanguageCodeFromHostname(): string | null {
    // console.log('+ SessionStorageService::determineLanguageCodeFromHostname');

    const portSuffix = window.location.port && `:${window.location.port}`;
    const currentHostName = window.location.hostname.toLowerCase() + portSuffix;
    // console.log('currentHostName=' + currentHostName);

    if (environment.hostNameEnglish.includes(currentHostName)) {
      // console.log('- SessionStorageService::determineLanguageCodeFromHostname: ' + Constants.LanguageCodeEnglish);
      return Constants.LanguageCodeEnglish;
    } else if (environment.hostNameFrench.includes(currentHostName)) {
      // console.log('- SessionStorageService::determineLanguageCodeFromHostname: ' + Constants.LanguageCodeFrench);
      return Constants.LanguageCodeFrench;
    }

    // console.log('- SessionStorageService::determineLanguageCodeFromHostname: null');
    return null;
  }

  private static getCurrentLanguageCode(): string | null {
    // console.log('+ SessionStorageService:getCurrentLanguageCode');

    const value: string | null = sessionStorage.getItem(Constants.SessionKeyCurrentLanguage);
    // MUST match at least one of valid values, or else ...
    if (value === Constants.LanguageCodeEnglish || value === Constants.LanguageCodeFrench) {
      // console.log('- SessionStorageService:getCurrentLanguageCode: ' + value);
      return value;
    }
    // ... we return null to indicate missing/invalid entry
    // console.log('- SessionStorageService:getCurrentLanguageCode: null');
    return null;
  }

  constructor() {
    // console.log('+ SessionStorageService::ctor');
    sessionStorage.setItem(Constants.SessionKeyCurrentLanguage, SessionStorageService.lastKnownLanguageCode);
    // console.log('- SessionStorageService::ctor');
  }

  clearAll(): void {
    //sessionStorage.clear();

    // clear entries individually instead of sessionStorage##clear to preserve
    // the currently selected language code

    this.clearAuthData();
    this.clearProductSubClassId();
    this.clearUserFullName();
  }

  getCurrentLocale(): string {
    return this.getCurrentLanguageCode() === Constants.LanguageCodeFrench
      ? Constants.LocaleFrenchCanada
      : Constants.LocaleEnglishCanada;
  }

  getCurrentLanguageCode(): string {
    return SessionStorageService.getCurrentLanguageCode()
      || SessionStorageService.lastKnownLanguageCode
      || SessionStorageService.determineLanguageCodeFromHostname()
      || Constants.DefaultLanguageCode;
  }

  //
  // Gets the code of the alternate language for eg. if currently set
  // language (based on session storage entry or, hostname match) is
  // "en", then "fr" is returned. And if it's "fr", then "en" is returned
  //
  getAltLanguageCode(): string {
    const currentLanguageCode = SessionStorageService.getCurrentLanguageCode()
      || SessionStorageService.determineLanguageCodeFromHostname()
      || Constants.DefaultLanguageCode;

    if (currentLanguageCode === Constants.LanguageCodeEnglish) {
      return Constants.LanguageCodeFrench;
    } else if (currentLanguageCode === Constants.LanguageCodeFrench) {
      return Constants.LanguageCodeEnglish;
    }

    // control should never reach here!!!
    throw new Error(`Unhandled case - ${currentLanguageCode}`);
  }

  toggleCurrentLanguageCode(reload: boolean = true): void {
    const altLanguageCode = this.getAltLanguageCode();

    sessionStorage.setItem(Constants.SessionKeyCurrentLanguage, altLanguageCode);
    SessionStorageService.lastKnownLanguageCode = altLanguageCode;

    if (reload && window && 'location' in window && typeof window.location.reload === 'function') {
      window.location.reload();
    }
  }

  clearAuthData(): void {
    sessionStorage.removeItem(Constants.SessionKeyAuthorizationData);
  }

  getAuthData(): AccountLoginResponse | null {
    let authData: any = sessionStorage.getItem(Constants.SessionKeyAuthorizationData);
    if (authData) {
      try {
        authData = <AccountLoginResponse>(JSON.parse(authData));
      } catch (e) {
        if (environment.production) {
          console.log(e);
        }
      }
    }
    return authData;
  }

  //
  // Convenience method to extract access token from the login response
  // object stored in session storage (if any)
  //
  getAuthToken(): string {
    const authData = this.getAuthData();
    return authData ? authData.access_token : '';
  }

  setAuthData(value: AccountLoginResponse) {
    sessionStorage.setItem(Constants.SessionKeyAuthorizationData, JSON.stringify(value));
  }

  clearProductSubClassId(): void { sessionStorage.removeItem(Constants.SessionKeyProductSubClass); }
  getProductSubClassId(): string | null { return sessionStorage.getItem(Constants.SessionKeyProductSubClass); }
  setProductSubClassId(value: string) { sessionStorage.setItem(Constants.SessionKeyProductSubClass, value); }

  //
  // Convenience method to get product plan name given a product's
  // sub-class ID. If subClassId is not provided, this method will
  // try to get the sub-class ID, if any, is stored in Session storage
  //
  getProductPlanName(subClassId?: number): string | null {
    if (typeof subClassId === 'undefined') {
      subClassId = +this.getProductSubClassId();
    }

    switch (subClassId) {
      case environment.subClassTotalPlan: return 'Total';
      case environment.subClassSystemsPlan: return 'Systems';
      case environment.subClassAppliancePlan: return 'Appliances';
      default:
        break;
    }

    return null;
  }

  isProductPlanTotal(): boolean { return +this.getProductSubClassId() === environment.subClassTotalPlan; }
  isProductPlanSystems(): boolean { return +this.getProductSubClassId() === environment.subClassSystemsPlan; }
  isProductPlanAppliances(): boolean { return +this.getProductSubClassId() === environment.subClassAppliancePlan; }

  clearUserFullName(): void { sessionStorage.removeItem(Constants.SessionKeyUserFullName); }
  getUserFullName(): string | null { return sessionStorage.getItem(Constants.SessionKeyUserFullName); }
  setUserFullName(value: string) { sessionStorage.setItem(Constants.SessionKeyUserFullName, value); }
}



// WEBPACK FOOTER //
// ./src/app/_services/sessionstorage.service.ts